import React, { useEffect, useState, useCallback } from "react"
import { Modal, Form, Button, Flex } from "antd"
import { useTranslation } from "react-i18next"
import { useWatch } from "antd/es/form/Form"
import DiscardConfirm from "ui/DiscardModal"
import { getGuideTrips } from "store/trips"
import { useDispatch, useSelector } from "react-redux"
import { createQuestionnaireTemplate, updateQuestionnaireTemplate, getQuestionnaireTemplates } from "store/question"
import { toaster } from "components/common/Toast"
import PropTypes from "prop-types"
import FirstLoading from "modules/loader-watchers/FirstLoading"
import { TemplateForm } from "./components/TemplateForm"
import { ExperienceSelector } from "./components/ExperienceSelector"
import { ExperienceDropdown } from "./components/ExperienceDropdown"
import { useNavigate } from "react-router-dom"
import { routes } from "router"

export const AddNewTemplate = ({ visible, onCancel, onSave, template, tab }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { items } = useSelector((state) => state.trips)
  const { loading } = useSelector((state) => state.question)
  const dropdownOptions = items.map((item) => ({
    label: item.title,
    value: item.id
  }))

  const [form] = Form.useForm()
  const experienceOptions = useWatch("experience", form, { defaultValue: [] })
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [showDiscard, setShowDiscard] = useState(false)
  const [errorMessages, setErrorMessages] = useState("")

  const renderExperienceDropdown = useCallback(
    () => <ExperienceDropdown dropdownOptions={dropdownOptions} experienceOptions={experienceOptions} form={form} />,
    [dropdownOptions, experienceOptions, form]
  )

  const isEditMode = !!template?.id
  const status = tab === "published" ? "published" : "draft"

  useEffect(() => {
    dispatch(getGuideTrips(null, { q: { status_eq: "published" } }))
  }, [dispatch])

  // Set form values when template changes (edit mode)
  useEffect(() => {
    if (template) {
      form.setFieldsValue({
        name: template.title,
        description: template.description,
        experience: template.publishedTags?.map((tag) => tag.value) || []
      })
    } else {
      form.resetFields()
    }
  }, [template, form])

  const handleOk = useCallback(() => {
    form.validateFields().then((values) => {
      const payload = {
        questionnaire_template: {
          name: values.name,
          description: values.description,
          trip_ids: values.experience || []
        }
      }

      const action = isEditMode ? updateQuestionnaireTemplate(template.id, payload) : createQuestionnaireTemplate(payload)

      dispatch(action)
        .then((res) => {
        toaster.success(res.message)
          navigate(routes.proSettingsTemplateDetails({ id: res.data.id }))
          dispatch(getQuestionnaireTemplates(1, 10, status))
          onSave?.()
          if (!isEditMode) {
            form.resetFields()
          }
        })
        .catch((error) => {
          setErrorMessages(error.data.errors.trip_ids)
        })
    })
  }, [form, isEditMode, template, dispatch, status, onSave])

  const handleCancel = useCallback(() => {
    form.resetFields()
    onCancel?.()
  }, [form, onCancel])

  // When modal cross icon is clicked, show discard confirm only if form is dirty
  const handleModalClose = useCallback(() => {
    if (form.isFieldsTouched()) {
      setShowDiscard(true)
    } else {
      handleCancel()
    }
  }, [form, handleCancel])

  // When discard is confirmed, close modal
  const handleDiscardConfirm = useCallback(() => {
    setShowDiscard(false)
    handleCancel()
  }, [handleCancel])

  // When discard is cancelled, just close discard modal
  const handleDiscardCancel = useCallback(() => {
    setShowDiscard(false)
  }, [])

  return (
    <>
      <Modal
        title={template?.id ? t("settings.edit_template") : t("settings.create_template")}
        open={visible}
        onCancel={handleModalClose}
        footer={null}
        destroyOnClose
        centered
        width={600}
      >
        <FirstLoading new={!loading}>
          <Form layout="vertical" form={form} preserve={false} onFinish={handleOk} style={{ marginTop: 16 }}>
            <TemplateForm />
            <ExperienceSelector
              form={form}
              dropdownOptions={dropdownOptions}
              experienceOptions={experienceOptions}
              dropdownOpen={dropdownOpen}
              setDropdownOpen={setDropdownOpen}
              renderExperienceDropdown={renderExperienceDropdown}
              errorMessages={errorMessages}
            />
            <Flex align="center" justify="flex-end" gap={12} style={{ marginTop: 32 }}>
              <Button color="primary" variant="outlined" onClick={handleModalClose} style={{ minWidth: 100 }}>
                {t("global.cancel") || "Cancel"}
              </Button>
              <Button color="primary" variant="solid" htmlType="submit" loading={loading} disabled={loading} style={{ minWidth: 160 }}>
                {t("settings.save_and_continue")}
              </Button>
            </Flex>
          </Form>
        </FirstLoading>
      </Modal>
      <DiscardConfirm
        open={showDiscard}
        onCancel={handleDiscardCancel}
        onConfirm={handleDiscardConfirm}
        title={t("Discard Changes?")}
        description={t("global.discard_changes")}
        t={t}
      />
    </>
  )
}

AddNewTemplate.propTypes = {
  visible: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func,
  template: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string,
    description: PropTypes.string,
    publishedTags: PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.string, PropTypes.shape({ value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) })])
    )
  }),
  tab: PropTypes.string
}
